import { SimpleSourceTag, UtilizationTag } from "@/types";

export interface RangeFilterColumn {
  id: string;
  label: string;
  field: string;
  minBound?: number;
  maxBound?: number;
}

export type RangeFilterState = Record<string, { min: number | null; max: number | null }>;

export const RANGE_FILTER_COLUMNS: RangeFilterColumn[] = [
  { id: "search_volume", label: "Search Volume", field: "search_volume" },
  { id: "kps", label: "KPS", field: "kps", minBound: 0, maxBound: 100 },
  // AUT-376: the Rel. column shows, sorts and filters the RAW relevance. hero_relevance_adjusted
  // is the penalty-discounted figure KPS scores against, surfaced in the KPS breakdown, not here.
  { id: "hero_relevance", label: "Relevance", field: "hero_relevance" },
  // AUT-496: the ticket's own example of what this filter is for — "SERP Match >= 65", i.e. show me
  // only the keywords whose page 1 looks like this product. Bounded 0-100: it is a percentage.
  { id: "serp_match_pct", label: "SERP Match", field: "serp_match_pct", minBound: 0, maxBound: 100 },
  { id: "search_volume_trend", label: "Trend", field: "search_volume_trend" },
  { id: "heroRank", label: "Hero Rank", field: "hero_rank" },
  { id: "competitors", label: "Competition", field: "competitors" },
];

export const DEFAULT_RANGE_FILTERS: RangeFilterState = {
  search_volume: { min: null, max: null },
  kps: { min: null, max: null },
  hero_relevance: { min: null, max: null },
  serp_match_pct: { min: null, max: null },
  search_volume_trend: { min: null, max: null },
  heroRank: { min: null, max: null },
  competitors: { min: null, max: null },
};

// AUT-419: free-text include/exclude filters shown in the Filters panel (Helium10-style).
export const TEXT_FILTER_COLUMNS = [
  {
    id: "phrases",
    label: "Phrases Containing",
    placeholder: "Ex: waterproof, tote",
    chipPrefix: "Contains",
    hint: "Comma-separated. Match Any or All of the terms.",
    matchModeKey: "phrasesMode",
  },
  {
    id: "exclude",
    label: "Exclude Keywords",
    placeholder: "Ex: kids, mini",
    chipPrefix: "Excludes",
    hint: "Hide phrases containing any of these (comma-separated).",
  },
];

export const FILTER_COLUMNS = [
  {
    id: "source",
    label: "Source",
    options: [
      { value: SimpleSourceTag.SQPR, label: "SQPR" },
      { value: SimpleSourceTag.MANUAL, label: "Manual" },
      // AUT-419: "Signal AI" removed from the Source column, so it's no longer a filterable source.
      { value: SimpleSourceTag.LANDSCAPE, label: "Landscape" },
      { value: SimpleSourceTag.SUBCATEGORY_DISCOVERY, label: "Sub-Category" },
    ],
  },
  {
    id: "utilization",
    label: "Utilization",
    options: [
      { value: UtilizationTag.TRACKED, label: "Tracked" },
      { value: UtilizationTag.A_B_TESTING, label: "A/B Testing" },
      { value: UtilizationTag.LANDSCAPED, label: "Landscaped" },
    ],
  },
]

export const DEFAULT_KEYWORD_FILTERS: Record<string, string[]> = {
  source: [],
  utilization: [],
  theme: [],
};

export interface KpsMaxScores {
  relevance: number;
  searchVolume: number;
  asinDensity: number;
  heroRank: number;
  searchTrend: number;
  keywordSales: number;
  competition: number;
}

/**
 * Fallback KPS factor max-scores for the KPS breakdown tooltip, used only
 * until `useGetKpsScoringConfig()` resolves (or if it errors) -- mirrors
 * `App\Support\DefaultLandscapeConfiguration` on the backend. The real,
 * possibly org-customized values come from `/kps-scoring-config`; these
 * exist purely to avoid a blank/zero flash while that request is in flight.
 */
// AUT-376: the shipped hero-relevance penalty multipliers, matching
// DefaultLandscapeConfiguration. Used only while /kps-scoring-config is still resolving, or on an
// estate whose config rows predate the two aliases.
export const DEFAULT_RELEVANCE_PENALTY_MULTIPLIERS = {
  competitorBrand: 0.5,
  noOverlap: 0.75,
} as const;

export const DEFAULT_KPS_MAX_SCORES: KpsMaxScores = {
  relevance: 37.5,
  searchVolume: 15,
  asinDensity: 15,
  heroRank: 15,
  searchTrend: 7.5,
  keywordSales: 10,
  competition: 15,
};

/**
 * The true, unconditional ceiling for a keyword's `kps` value --
 * `LEAST(FLOOR(sum of the 6 factor scores), 100)` in
 * 06_calc_kps_for_keywords.sql caps it at 100 no matter what the org's
 * configured factor maxes sum to. `UpdateConfigurationSettingsRequest` only
 * bounds each factor max individually to [0, 100]; nothing enforces that the
 * six of them add up to exactly 100. So the KPS breakdown tooltip's overall
 * Total must always be shown out of this constant, never out of the sum of
 * the (possibly non-100-summing) configured maxes -- otherwise a keyword
 * that hits its true ceiling would display as e.g. "100 / 107", which reads
 * as under, not at, the max.
 */
export const KPS_MAX_SCORE = 100;

/**
 * The three strings the KPS breakdown popover renders, pinned verbatim by
 * `decisions/the-kps-breakdown-renders-a-view-model.md` and
 * `components/rank-tracker.md § The three breakdown strings, pinned` --
 * `sources.md` cannot supply any of them (its factor list is the fabricated
 * six-term one BR38 also records, now marked stale). Kept beside
 * `DEFAULT_KPS_MAX_SCORES` because both are constants the popover renders
 * from, per that section.
 */

/** FR-8b, keyword-arm only: renders when the factors do not reconcile to the total. */
export const KPS_BREAKDOWN_UNRECONCILED_TEXT =
  "Factors with no data are left out of the scale, so the points above don't add up to the total.";

/** FR-9, roll-up arm only: states the row's base directly beneath the header. */
export function formatKpsBreakdownAverageOverKeywords(keywordCount: number): string {
  return `Average over ${keywordCount} keyword${keywordCount === 1 ? "" : "s"}.`;
}

/**
 * FR-10, roll-up arm only: replaces the single-keyword penalty sentence in the footer's penalty
 * slot. `{c}` (penaltyKeywordCount) and `{N}` (keywordCount) pluralise independently -- the verb
 * follows `{c}`, the noun follows `{N}` -- so `{c} = 1, {N} = 1` renders "1 of 1 keyword has a
 * reduced Relevance Score." Callers render `No penalties applied.` instead when `{c} === 0`.
 */
export function formatKpsBreakdownPenaltyKeywordCount(penaltyKeywordCount: number, keywordCount: number): string {
  const noun = keywordCount === 1 ? "keyword" : "keywords";
  const verb = penaltyKeywordCount === 1 ? "has" : "have";
  return `${penaltyKeywordCount} of ${keywordCount} ${noun} ${verb} a reduced Relevance Score.`;
}